import '@/lib/install-capture'; // POS-I299: first, so the /m install event and manifest are caught at page load
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/lib/query-client';
import { initSessionBoundary } from '@/lib/session-boundary';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster } from 'react-hot-toast';
import { PrimeReactProvider } from 'primereact/api';
import 'primereact/resources/themes/lara-light-indigo/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
// Initialize i18n before rendering (must be imported before App)
import '@/lib/i18n';
import { useI18nStore } from '@/stores/i18n.store';
import '@/stores/appearance.store'; // POS-I226: applies the cached text size, density and colour before the first paint
import App from './App';
import { TooltipProvider } from '@/components/ui/tooltip';
import './index.css';
import { startVersionWatch } from '@/lib/versionWatch';

// POS-I109: every change of signed-in user or store (login, logout, refresh failure,
// tenant switch, impersonation, another tab) clears the client's tenant data.
initSessionBoundary();
startVersionWatch(); // POS-I311: 'New version ready, Update' prompt (never reloads by itself)

function DynamicToaster() {
  const dir = useI18nStore((s) => s.dir);
  return (
    <Toaster
      position={dir === 'rtl' ? 'top-left' : 'top-right'}
      toastOptions={{
        duration: 3000,
        style: {
          background: 'hsl(var(--card))',
          color: 'hsl(var(--card-foreground))',
          border: '1px solid hsl(var(--border))',
          borderRadius: '0.75rem',
          fontSize: '0.875rem',
          fontWeight: '500',
          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
        },
      }}
    />
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <PrimeReactProvider value={{ ripple: true }}>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <TooltipProvider delayDuration={300}>
          <App />
          <DynamicToaster />
        </TooltipProvider>
      </BrowserRouter>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
    </PrimeReactProvider>
  </React.StrictMode>,
);
